import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Grid,
  List, ListItem, ListItemButton, ListItemText, alpha, IconButton, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getNoticePaper, getNoticePaperNavigation } from '../services/api';

// CSS classes that indicate section headings in Notice Papers
const HEADING_CLASSES = [
  'DPSSectionHeading',
  'DPSOTDHeading',
  'DPSNoticesHeading',
  'DPSQuestSectionHeading',
  'DPSCommitteeTypeHeading',
  'DPSStandingCommitteeSubTypeHeading'
];

export default function NoticePaperDetail({ chamber = 'House' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTocItem, setActiveTocItem] = useState(null);
  const [navigation, setNavigation] = useState({ prev: null, next: null });
  const [tocItems, setTocItems] = useState([]);

  // Refs for scroll tracking
  const contentRef = useRef(null);
  const isScrollingRef = useRef(false);

  const isHouse = chamber === 'House';
  const themeColor = isHouse ? '#4caf50' : '#c62828';
  const backPath = isHouse ? '/house-notice-papers' : '/senate-notice-papers';
  const detailPath = isHouse ? '/house-notice-papers' : '/senate-notice-papers';
  const chamberLabel = isHouse ? 'House of Representatives' : 'Senate';

  useEffect(() => {
    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      setTocItems([]);
      try {
        const [data, nav] = await Promise.all([
          getNoticePaper(id),
          getNoticePaperNavigation(id).catch(() => ({ prev: null, next: null }))
        ]);
        setDocument(data);
        setNavigation(nav);
      } catch (err) {
        console.error('Error fetching notice paper:', err);
        setError('Failed to load notice paper');
      }
      setLoading(false);
    };
    fetchDocument();
  }, [id]);

  // Extract TOC from HTML content by parsing heading classes
  const extractTocFromHtml = useCallback((htmlContent) => {
    if (!htmlContent) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const items = [];

    // Find all elements with heading classes
    const selector = HEADING_CLASSES.map(cls => `.${cls}`).join(', ');
    const headings = doc.querySelectorAll(selector);

    headings.forEach((heading, index) => {
      const text = heading.textContent?.trim();
      if (text && text.length > 2 && text.length < 150) {
        items.push({
          id: `toc-section-${index}`,
          title: text,
          index: index,
          className: heading.className
        });
      }
    });

    return items;
  }, []);

  // Extract TOC from plain text content (fallback)
  const extractTocFromText = useCallback((content) => {
    if (!content) return [];

    const items = [];
    const lines = content.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Check for all-caps section headers or numbered sections
      if (line.length > 3 && line.length < 100) {
        if ((line === line.toUpperCase() && /^[A-Z]/.test(line)) ||
            /^\d+\s+[A-Z]/.test(line)) {
          items.push({
            id: `toc-section-${i}`,
            title: line.replace(/^\d+\s+/, ''), // Remove leading numbers
            index: i
          });
        }
      }
    }

    return items;
  }, []);

  // Extract TOC when document loads
  useEffect(() => {
    if (document) {
      const items = document.content_html
        ? extractTocFromHtml(document.content_html)
        : extractTocFromText(document.content);
      setTocItems(items);
      // Set first item as active by default
      if (items.length > 0 && !activeTocItem) {
        setActiveTocItem(items[0].id);
      }
    }
  }, [document, extractTocFromHtml, extractTocFromText, activeTocItem]);

  // Setup IntersectionObserver to track active section while scrolling
  useEffect(() => {
    if (tocItems.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -60% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      // Don't update if user clicked a TOC item (manual scroll in progress)
      if (isScrollingRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTocItem(entry.target.id);
        }
      });
    }, observerOptions);

    // Observe all section anchors
    tocItems.forEach((item) => {
      const element = window.document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [tocItems]);

  const handleNavigate = (docId) => {
    navigate(`${detailPath}/${docId}`);
  };

  const formatNavDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };


  // Scroll to a section when clicking TOC item
  const scrollToSection = useCallback((sectionId) => {
    isScrollingRef.current = true;
    setActiveTocItem(sectionId);

    const element = window.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Reset scrolling flag after animation
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 1000);
    } else {
      isScrollingRef.current = false;
    }
  }, []);

  // Process HTML to inject section IDs for scrolling
  const processHtmlWithSectionIds = useCallback((htmlContent, items) => {
    if (!htmlContent || items.length === 0) return htmlContent;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    // Find all heading elements and add IDs
    const selector = HEADING_CLASSES.map(cls => `.${cls}`).join(', ');
    const headings = doc.querySelectorAll(selector);

    headings.forEach((heading, index) => {
      if (index < items.length) {
        // Create a wrapper div with the section ID
        const wrapper = doc.createElement('div');
        wrapper.id = items[index].id;
        wrapper.className = 'toc-section-anchor';
        wrapper.style.scrollMarginTop = '100px';
        heading.parentNode.insertBefore(wrapper, heading);
        wrapper.appendChild(heading);
      }
    });

    return doc.body.innerHTML;
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: themeColor }} />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Notice paper not found'}</Typography>
        <Button component={RouterLink} to={backPath} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Notice Papers
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Navigation Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to={backPath}
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to {chamberLabel} Notice Papers
        </Button>

        {/* Prev/Next Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={navigation.prev ? `Previous: ${formatNavDate(navigation.prev.date)}` : 'No previous document'}>
            <span>
              <IconButton
                onClick={() => navigation.prev && handleNavigate(navigation.prev.id)}
                disabled={!navigation.prev}
                sx={{
                  bgcolor: navigation.prev ? alpha(themeColor, 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha(themeColor, 0.2) }
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {formatDate(document?.date)}
          </Typography>
          <Tooltip title={navigation.next ? `Next: ${formatNavDate(navigation.next.date)}` : 'No next document'}>
            <span>
              <IconButton
                onClick={() => navigation.next && handleNavigate(navigation.next.id)}
                disabled={!navigation.next}
                sx={{
                  bgcolor: navigation.next ? alpha(themeColor, 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha(themeColor, 0.2) }
                }}
              >
                <NavigateNextIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(themeColor, 0.08)} 0%, ${alpha(themeColor, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(themeColor, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: themeColor }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: themeColor,
                mb: 0.5
              }}
            >
              {document.title || 'Notice Paper'}
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {formatDate(document.date)}
            </Typography>

            {/* Metadata chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label={chamberLabel}
                size="small"
                sx={{ bgcolor: themeColor, color: 'white' }}
              />
              {document.parliament_number && (
                <Chip
                  label={`${document.parliament_number}th Parliament`}
                  size="small"
                  variant="outlined"
                />
              )}
              {document.notice_paper_number && (
                <Chip
                  label={`No. ${document.notice_paper_number}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {document.source_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={document.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderColor: themeColor, color: themeColor }}
                >
                  View on ParlInfo
                </Button>
              )}
              {document.pdf_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={document.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Table of Contents */}
        {tocItems.length > 0 && (
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 80,
                maxHeight: 'calc(100vh - 120px)',
                overflow: 'auto'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: themeColor }}>
                Contents
              </Typography>
              <List dense disablePadding>
                {tocItems.map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      selected={activeTocItem === item.id}
                      onClick={() => scrollToSection(item.id)}
                      sx={{
                        borderLeft: activeTocItem === item.id
                          ? `3px solid ${themeColor}`
                          : '3px solid transparent',
                        py: 0.75
                      }}
                    >
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: activeTocItem === item.id ? 600 : 400,
                          textTransform: 'capitalize'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Main Content */}
        <Grid item xs={12} md={tocItems.length > 0 ? 9 : 12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              ref={contentRef}
              sx={{
                '& p': { mb: 2, lineHeight: 1.8 },
                '& pre': {
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  lineHeight: 1.6
                },
                '& .toc-section-anchor': {
                  scrollMarginTop: '100px'
                },
                // Styling for DPS heading classes
                '& .DPSSectionHeading, & .DPSOTDHeading, & .DPSNoticesHeading': {
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: themeColor,
                  mt: 3,
                  mb: 1
                },
                '& .DPSQuestSectionHeading, & .DPSCommitteeTypeHeading': {
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'text.primary',
                  mt: 2,
                  mb: 1
                },
                lineHeight: 1.8,
                fontSize: '0.95rem'
              }}
            >
              {document.content_html ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: processHtmlWithSectionIds(document.content_html, tocItems)
                  }}
                />
              ) : (
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    m: 0,
                    lineHeight: 1.8
                  }}
                >
                  {document.content}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
